import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { jobAPI, employerAPI } from '../../services/api';
import { FaArrowLeft, FaBriefcase, FaExclamationTriangle } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import Button from '../../components/Button';
import { useAuth } from '../../contexts/AuthContext';
import { validateForm as validateFormUtil } from '../../utils/validation';

const PostJob = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        requirements: '',
        job_type: 'full_time',
        location: '',
        salary_range: '',
        application_deadline: '',
        is_remote: false
    });
    const [errors, setErrors] = useState({});
    const [employer, setEmployer] = useState(null);
    const [checkingKYC, setCheckingKYC] = useState(true);
    const { user } = useAuth();

    useEffect(() => {
        const checkKYCStatus = async () => {
            try {
                const response = await employerAPI.getProfile();
                if (response.success && response.employer) {
                    setEmployer(response.employer);

                    // Redirect to KYC if not approved
                    if (response.employer.kyc_status !== 'approved') {
                        toast.error('Please complete KYC verification before posting jobs');
                        navigate('/employer/kyc');
                        return;
                    }
                }
            } catch (err) {
                console.error('Failed to check KYC status:', err);
            } finally {
                setCheckingKYC(false);
            }
        };

        if (user?.role === 'employer') {
            checkKYCStatus();
        } else {
            setCheckingKYC(false);
        }
    }, [user, navigate]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const validate = () => {
        const rules = {
            title: { required: true, minLength: 5, label: 'Job Title' },
            description: { required: true, minLength: 50, label: 'Job Description' },
            requirements: { required: true, minLength: 20, label: 'Requirements' },
            location: { required: true, label: 'Location' },
        };
        const newErrors = validateFormUtil(formData, rules);
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validate()) {
            toast.error('Please fix the errors in the form');
            return;
        }

        // Double-check KYC status before submitting
        if (employer && employer.kyc_status !== 'approved') {
            toast.error('Your KYC verification must be approved before posting jobs');
            navigate('/employer/kyc');
            return;
        }

        setLoading(true);

        try {
            await jobAPI.create(formData);
            toast.success('Job posted successfully!');
            navigate('/dashboard');
        } catch (err) {
            console.error('Failed to post job:', err);
            const errorMessage = err?.response?.data?.error || err?.message || 'Failed to post job. Please try again.';
            toast.error(errorMessage);

            // If error is about KYC, redirect to KYC page
            if (errorMessage.includes('KYC') || errorMessage.includes('approved')) {
                navigate('/employer/kyc');
            }
        } finally {
            setLoading(false);
        }
    };

    if (checkingKYC) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Checking verification status...</p>
                </div>
            </div>
        );
    }

    if (employer && employer.kyc_status !== 'approved') {
        return (
            <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
                <div className="max-w-4xl mx-auto px-6 py-8">
                    <Link to="/dashboard" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-6">
                        <FaArrowLeft className="mr-2" /> Back to Dashboard
                    </Link>
                    <div className="bg-white rounded-xl shadow-lg p-8">
                        <div className="text-center py-12">
                            <FaExclamationTriangle className="text-6xl text-yellow-500 mx-auto mb-4" />
                            <h2 className="text-2xl font-bold text-gray-800 mb-2">KYC Verification Required</h2>
                            <p className="text-gray-600 mb-6">
                                Your KYC verification must be approved before you can post jobs.
                            </p>
                            <Link
                                to="/employer/kyc"
                                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                            >
                                Complete KYC Verification
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
            <div className="max-w-4xl mx-auto px-6 py-8">
                <Link to="/dashboard" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-6">
                    <FaArrowLeft className="mr-2" /> Back to Dashboard
                </Link>

                <div className="bg-white rounded-xl shadow-lg p-8">
                    <div className="flex items-center gap-3 mb-6">
                        <FaBriefcase className="text-3xl text-primary-600" />
                        <h1 className="text-3xl font-bold text-gray-900">Post a Job</h1>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Job Title *
                            </label>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent ${errors.title ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                placeholder="e.g., Senior Software Engineer"
                            />
                            {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Job Description *
                            </label>
                            <textarea
                                name="description"
                                rows={6}
                                value={formData.description}
                                onChange={handleChange}
                                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent ${errors.description ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                placeholder="Describe the role, responsibilities, and what you're looking for..."
                            />
                            {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description}</p>}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Requirements *
                            </label>
                            <textarea
                                name="requirements"
                                rows={4}
                                value={formData.requirements}
                                onChange={handleChange}
                                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent ${errors.requirements ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                placeholder="List the required skills, experience, and qualifications..."
                            />
                            {errors.requirements && <p className="text-red-500 text-xs mt-1">{errors.requirements}</p>}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Job Type *
                                </label>
                                <select
                                    name="job_type"
                                    value={formData.job_type}
                                    onChange={handleChange}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                                >
                                    <option value="full_time">Full Time</option>
                                    <option value="part_time">Part Time</option>
                                    <option value="internship">Internship</option>
                                    <option value="contract">Contract</option>
                                    <option value="freelance">Freelance</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Location *
                                </label>
                                <input
                                    type="text"
                                    name="location"
                                    value={formData.location}
                                    onChange={handleChange}
                                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent ${errors.location ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                    placeholder="e.g., Lagos, Nigeria"
                                />
                                {errors.location && <p className="text-red-500 text-xs mt-1">{errors.location}</p>}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Salary Range
                                </label>
                                <input
                                    type="text"
                                    value={formData.salary_range}
                                    onChange={(e) => setFormData({ ...formData, salary_range: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                                    placeholder="e.g., ₦500,000 - ₦800,000"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Application Deadline
                                </label>
                                <input
                                    type="date"
                                    value={formData.application_deadline}
                                    onChange={(e) => setFormData({ ...formData, application_deadline: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                                />
                            </div>
                        </div>

                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                id="is_remote"
                                checked={formData.is_remote}
                                onChange={(e) => setFormData({ ...formData, is_remote: e.target.checked })}
                                className="h-4 w-4 text-primary-600 focus:ring-primary-600 border-gray-300 rounded"
                            />
                            <label htmlFor="is_remote" className="ml-2 block text-sm text-gray-700">
                                Remote work available
                            </label>
                        </div>

                        <div className="flex gap-4 pt-4">
                            <Button type="submit" variant="primary-gradient" className="flex-1 px-6 py-3">
                                {loading ? 'Posting...' : 'Post Job'}
                            </Button>
                            <button
                                type="button"
                                onClick={() => navigate('/dashboard')}
                                className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default PostJob;

